
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Role, Candidate } from "../candidate-types";

export const useProjectData = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const [projectName, setProjectName] = useState<string>("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [candidatesPerRole, setCandidatesPerRole] = useState<{ [roleId: string]: Candidate[]; }>({});

  useEffect(() => {
    const fetchRoles = async () => {
      if (!projectId) {
        toast.error("Project ID is required");
        return;
      }
      setRolesLoading(true);
      try {
        const { data: projectData, error: projectError } = await supabase
          .from('project_requirements')
          .select('name')
          .eq('project_id', projectId)
          .maybeSingle();

        if (projectError) {
          console.error('Error fetching project:', projectError);
          toast.error("Failed to fetch project details");
          throw projectError;
        }

        if (projectData) {
          setProjectName(projectData.name);
        }

        const { data: roleData, error: roleError } = await supabase
          .from('role_jd')
          .select('*')
          .eq('project_id', projectId);

        if (roleError) {
          console.error('Error fetching roles:', roleError);
          toast.error("Failed to fetch roles");
          throw roleError;
        }

        const transformedRoles: Role[] = roleData?.filter(role => {
          if (!role.role_description || typeof role.role_description !== 'object') {
            return false;
          }
          
          const roleDesc = role.role_description as any;
          const hasPdf = roleDesc.pdf_url;
          const hasValidText = roleDesc.text && 
                              roleDesc.text.trim() !== '' && 
                              roleDesc.text !== 'No JD found';
          
          return hasPdf || hasValidText;
        }).map(role => ({
          id: role.id,
          role_title: role.role_title,
          project_name: role.name,
          project_id: role.project_id,
          name: role.name,
          role_description: {
            text: typeof role.role_description === 'object' ? (role.role_description as any).text || '' : '',
            pdf_url: typeof role.role_description === 'object' ? (role.role_description as any).pdf_url : undefined,
            pdf_filename: typeof role.role_description === 'object' ? (role.role_description as any).pdf_filename : undefined
          }
        })) || [];

        setRoles(transformedRoles);

        if (transformedRoles.length === 0) {
          toast.info("No roles with JDs found");
        }
      } catch (error) {
        console.error('Error in fetchRoles:', error);
        toast.error("Failed to fetch roles");
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, [projectId]);

  useEffect(() => {
    const fetchExistingCandidates = async () => {
      if (!projectId || !roles.length) {
        return;
      }
      
      try {
        const { data: existingCandidates, error } = await supabase
          .from('candidates')
          .select('*')
          .eq('project_id', projectId);

        if (error) {
          console.error('Error fetching existing candidates:', error);
          return;
        }

        const candidatesByRole = roles.reduce((acc, role) => {
          const roleCandidates = existingCandidates?.filter(c => c.role_title === role.role_title)
            // Standardized sorting: match percentage (desc), then candidate_id (asc)
            ?.sort((a, b) => {
              const matchPercentageA = parseInt(a.match_percentage || '0');
              const matchPercentageB = parseInt(b.match_percentage || '0');
              
              if (matchPercentageA !== matchPercentageB) {
                return matchPercentageB - matchPercentageA; // Higher percentage first
              }
              
              // If match percentages are equal, sort by candidate_id ascending
              return (a.candidate_id || 0) - (b.candidate_id || 0);
            })
            ?.map(c => {
              let differentiators: string[] = [];
              if (Array.isArray(c.differentiators)) {
                differentiators = c.differentiators.map(d => String(d));
              } else if (c.differentiators) {
                differentiators = [String(c.differentiators)];
              }

              console.log(`[useProjectData] Processing candidate ${c.candidate_name} for role ${role.role_title}:`, {
                match_percentage: c.match_percentage,
                candidate_id: c.candidate_id
              });

              return {
                id: c.candidate_id,
                name: c.candidate_name || '',
                project_name: c.project_name,
                project_id: c.project_id,
                candidate_name: c.candidate_name || '',
                roleTitle: c.role_title,
                pdfUrl: '',
                matchPercentage: parseInt(c.match_percentage || '0'),
                summary: c.summary || '',
                differentiators,
                linkedinUrl: c.linkedin_url || '',
                openToWork: c.open_to_work || false,
                profilePictureUrl: c.profile_picture_url
              };
            }) || [];

          console.log(`[useProjectData] Final candidates for role ${role.role_title}:`, roleCandidates.map(c => ({
            name: c.candidate_name,
            matchPercentage: c.matchPercentage,
            candidate_id: c.id
          })));

          acc[role.id] = roleCandidates;
          return acc;
        }, {} as { [roleId: string]: Candidate[]; });

        setCandidatesPerRole(candidatesByRole);
      } catch (error) {
        console.error('Error in fetchExistingCandidates:', error);
      }
    };

    fetchExistingCandidates();
  }, [projectId, roles]);

  return {
    projectId,
    projectName,
    roles,
    rolesLoading,
    candidatesPerRole,
    setCandidatesPerRole
  };
};
